import React, { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { ChoiceGroup, IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { ProgressIndicator } from "@fluentui/react/lib/ProgressIndicator";
import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar";
import { Separator } from "@fluentui/react/lib/Separator";
import { Icon } from "@fluentui/react/lib/Icon";
import { saveAs } from "file-saver";

import { TroubleshootingToolProps } from "../../troubleshooting-tools.types";
import { formatMessageDefault } from "../../../../../utils/helpers/LanguageHelper";
import { useAppSelector } from "../../../../../redux/hooks/hooks";
import { selectClientId, selectCpmUsername } from "../../../../../redux/feature/settings/configurations-slice";
import { getBackendUrl } from "../../../../../utils/axios";
import { DiagnosticConnectionHandle, openDiagnosticConnection } from "../../../../../utils/helpers/SseDiagnosticHelper";
import {
  DiagnosticMessage,
  DiagnosticSegment,
  PhaseMessage,
  SegmentRun,
  SseTestAction,
  SseTestState,
  TestMode,
  VerdictState,
} from "./sse-test.types";
import {
  buildReportMarkdown,
  buildTimelineJson,
  computeReport,
  formatClock,
  formatDuration,
  LOCAL_ESTIMATE_MS,
  ReportMeta,
  SEGMENTS_FOR_MODE,
} from "./sse-test.helper";

const initialState: SseTestState = {
  mode: "threshold",
  runPhase: "idle",
  statusText: "",
  elapsedMs: 0,
  report: null,
  error: null,
};

function reducer(state: SseTestState, action: SseTestAction): SseTestState {
  switch (action.type) {
    case "SET_MODE":
      return { ...state, mode: action.payload };
    case "START":
      return { ...state, runPhase: "running", statusText: "", elapsedMs: 0, report: null, error: null };
    case "STATUS":
      return { ...state, statusText: action.payload };
    case "TICK":
      return { ...state, elapsedMs: action.payload };
    case "COMPLETED":
      return { ...state, runPhase: "completed", report: action.payload, statusText: "" };
    case "ERROR":
      return { ...state, runPhase: "error", error: action.payload, statusText: "" };
    case "ABORTED":
      return { ...initialState, mode: state.mode };
    case "RESET":
      return { ...initialState, mode: state.mode };
    default:
      return state;
  }
}

function generateTestId(): string {
  // testId is used only to correlate this run with the server-side logs (no security impact), but we still source its
  // randomness from the Web Crypto CSPRNG to keep SonarQube's S2245 hotspot clear. window.msCrypto covers legacy
  // IE11-based Office WebViews where window.crypto is not exposed.
  const cryptoObj: Crypto | undefined =
    typeof window !== "undefined" ? window.crypto || (window as unknown as { msCrypto?: Crypto }).msCrypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    const array = new Uint32Array(1);
    cryptoObj.getRandomValues(array);
    return `test-${Date.now()}-${(array[0] ?? 0).toString(36)}`;
  }
  return `test-${Date.now()}`;
}

const MODE_LABELS: Record<TestMode, { key: string; fallback: string }> = {
  quick: { key: "cof.troubleshooting.ssetest.mode.quick", fallback: "Quick check" },
  threshold: { key: "cof.troubleshooting.ssetest.mode.threshold", fallback: "5-minute threshold (Pass/Fail)" },
  ramp: { key: "cof.troubleshooting.ssetest.mode.ramp", fallback: "Progressive diagnostic" },
};

const MODE_DESCRIPTIONS: Record<TestMode, { key: string; fallback: string }> = {
  quick: {
    key: "cof.troubleshooting.ssetest.desc.quick",
    fallback:
      "Fast smoke test, representative of the real channel (immediate first byte): verifies the channel opens and carries events smoothly over a short quiet period.",
  },
  threshold: {
    key: "cof.troubleshooting.ssetest.desc.threshold",
    fallback:
      "Official verdict, representative of the real channel: immediate first byte, then survive a ~4-minute quiet period (our renewal window) with no response buffering. Stops early if a limit is hit.",
  },
  ramp: {
    key: "cof.troubleshooting.ssetest.desc.ramp",
    fallback:
      "Advanced raw diagnostic: probes a delayed first byte and progressive silences (30s -> 4min), one short connection each, to estimate the network's actual first-byte and idle thresholds.",
  },
};

const stateIcon = (state: VerdictState): string => {
  switch (state) {
    case "PASS":
      return "SkypeCircleCheck";
    case "FAIL":
      return "StatusErrorFull";
    case "NOT_TESTED":
      return "Blocked2";
    default:
      return "InfoSolid";
  }
};

const stateColor = (state: VerdictState): string => {
  switch (state) {
    case "PASS":
      return "var(--color-green, #107c10)";
    case "FAIL":
      return "var(--color-red, #a4262c)";
    default:
      return "var(--color-grey, #605e5c)";
  }
};

export const SseTest: React.FC<TroubleshootingToolProps> = ({ onError, onSuccess }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const clientId = useAppSelector(selectClientId);
  const userName = useAppSelector(selectCpmUsername);

  const activeHandleRef = useRef<DiagnosticConnectionHandle | null>(null);
  const cancelledRef = useRef(false);
  const runStartMsRef = useRef(0);
  /** Settles the in-flight segment promise on abort (closes the EventSource and resolves with the partial run). */
  const abortSegmentRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      // Unmount (e.g. accordion collapsed): stop any in-flight diagnostic so it cannot leak a connection.
      cancelledRef.current = true;
      abortSegmentRef.current?.();
      activeHandleRef.current?.close();
      activeHandleRef.current = null;
    };
  }, []);

  // Progress timer: while running, tick elapsed time so the progress bar advances toward the local estimate.
  useEffect(() => {
    if (state.runPhase !== "running") {
      return;
    }
    const intervalId = setInterval(() => {
      dispatch({ type: "TICK", payload: Date.now() - runStartMsRef.current });
    }, 500);
    return () => clearInterval(intervalId);
  }, [state.runPhase]);

  const meta: ReportMeta = useMemo(() => ({ backendUrl: getBackendUrl(), userName, clientId }), [userName, clientId]);

  const runSegment = useCallback(
    (mode: TestMode, segment: DiagnosticSegment, testId: string, step = 0): Promise<SegmentRun> =>
      new Promise((resolve) => {
        const start = Date.now();
        const run: SegmentRun = { segment, hadOpen: false, gotDone: false, errored: false, events: [] };
        let settled = false;

        const finish = () => {
          if (settled) return;
          settled = true;
          abortSegmentRef.current = null;
          activeHandleRef.current?.close();
          activeHandleRef.current = null;
          resolve(run);
        };
        // Allow an external abort to settle this segment cleanly (close + resolve the partial run).
        abortSegmentRef.current = finish;

        openDiagnosticConnection(
          { mode, segment, testId, step },
          {
            onOpen: () => {
              run.hadOpen = true;
              run.onOpenMs = Date.now() - start;
              dispatch({
                type: "STATUS",
                payload: formatMessageDefault(
                  "cof.troubleshooting.ssetest.status.connected",
                  "Connected, running probe..."
                ),
              });
            },
            onMessage: (data) => {
              const message = data as DiagnosticMessage;
              run.events.push({ message, recvMs: Date.now() - start });
              if (message.type === "plan") {
                run.plan = message;
              } else if (message.type === "phase") {
                const phase = message as PhaseMessage;
                if (phase.name === "idle" && phase.state === "start") {
                  dispatch({
                    type: "STATUS",
                    payload: formatMessageDefault(
                      "cof.troubleshooting.ssetest.status.idle",
                      "Holding silence for {gap} (idle test)..."
                    ).replace("{gap}", formatDuration(phase.expectedGapMs)),
                  });
                }
              } else if (message.type === "done") {
                run.gotDone = true;
                finish();
              }
            },
            onError: () => {
              run.errored = true;
              run.errorMs = Date.now() - start;
              finish();
            },
          }
        )
          .then((handle) => {
            if (settled) {
              handle.close();
            } else {
              activeHandleRef.current = handle;
            }
          })
          .catch(() => {
            // openDiagnosticConnection can reject when auth headers cannot be obtained (no valid token).
            run.errored = true;
            run.errorMs = Date.now() - start;
            finish();
          });
      }),
    []
  );

  const runDiagnostic = useCallback(async () => {
    cancelledRef.current = false;
    runStartMsRef.current = Date.now();
    dispatch({ type: "START" });
    const testId = generateTestId();
    const startedAtIso = new Date().toISOString();
    const segments: SegmentRun[] = [];

    try {
      for (const segment of SEGMENTS_FOR_MODE[state.mode]) {
        if (cancelledRef.current) return;
        dispatch({
          type: "STATUS",
          payload: formatMessageDefault(
            "cof.troubleshooting.ssetest.status.opening",
            "Opening {segment} segment..."
          ).replace("{segment}", segment),
        });
        segments.push(await runSegment(state.mode, segment, testId));
      }

      if (state.mode === "ramp") {
        // Ramp is the raw diagnostic: first a silent first-byte probe, then one short stream connection per idle gap.
        // Each connection is bounded by the renewal window. stepCount is learned from the first stream plan; we stop at
        // the first gap that drops (the customer's timeout threshold).
        if (cancelledRef.current) return;
        dispatch({
          type: "STATUS",
          payload: formatMessageDefault(
            "cof.troubleshooting.ssetest.status.firstByte",
            "Probing delayed first byte..."
          ),
        });
        segments.push(await runSegment(state.mode, "first-byte", testId));

        let step = 0;
        let stepCount = 1;
        do {
          if (cancelledRef.current) return;
          dispatch({
            type: "STATUS",
            payload: formatMessageDefault(
              "cof.troubleshooting.ssetest.status.rampStep",
              "Probing silence step {step}..."
            ).replace("{step}", String(step + 1)),
          });
          const run = await runSegment(state.mode, "stream", testId, step);
          segments.push(run);
          if (step === 0 && run.plan?.stepCount) {
            stepCount = run.plan.stepCount;
          }
          if (!run.gotDone) break; // this gap dropped => threshold found, stop climbing
          step++;
        } while (step < stepCount);
      }

      if (cancelledRef.current) return;
      const report = computeReport(state.mode, testId, startedAtIso, segments);
      dispatch({ type: "COMPLETED", payload: report });
      onSuccess?.(
        formatMessageDefault("cof.troubleshooting.ssetest.onSuccess", "SSE diagnostic complete: {verdict}").replace(
          "{verdict}",
          report.overall
        )
      );
    } catch (e) {
      const messageText = e instanceof Error ? e.message : "Unknown error";
      dispatch({ type: "ERROR", payload: messageText });
      onError?.(e instanceof Error ? e : new Error(messageText));
    }
  }, [state.mode, runSegment, onError, onSuccess]);

  const abortDiagnostic = useCallback(() => {
    // Client-initiated abort: stop the loop, settle+close the in-flight segment (which drops the connection so the
    // server cancels its worker via onError/onCompletion), and reset the UI to idle. Nothing is left running.
    cancelledRef.current = true;
    abortSegmentRef.current?.();
    dispatch({ type: "ABORTED" });
  }, []);

  const modeOptions: IChoiceGroupOption[] = useMemo(
    () =>
      (Object.keys(MODE_LABELS) as TestMode[]).map((mode) => ({
        key: mode,
        text: `${formatMessageDefault(MODE_LABELS[mode].key, MODE_LABELS[mode].fallback)} (~${formatDuration(
          LOCAL_ESTIMATE_MS[mode]
        )} max)`,
      })),
    []
  );

  const exportMarkdown = useCallback(() => {
    if (!state.report) return;
    const blob = new Blob([buildReportMarkdown(state.report, meta)], { type: "text/markdown;charset=utf-8" });
    saveAs(blob, `sse-diagnostic-${state.report.testId}.md`);
    onSuccess?.(formatMessageDefault("cof.troubleshooting.ssetest.export.report", "Report exported."));
  }, [state.report, meta, onSuccess]);

  const exportTimeline = useCallback(() => {
    if (!state.report) return;
    const blob = new Blob([buildTimelineJson(state.report)], { type: "application/json;charset=utf-8" });
    saveAs(blob, `sse-diagnostic-${state.report.testId}.json`);
    onSuccess?.(formatMessageDefault("cof.troubleshooting.ssetest.export.timeline", "Timeline exported."));
  }, [state.report, onSuccess]);

  const copyReport = useCallback(async () => {
    if (!state.report) return;
    try {
      await navigator.clipboard.writeText(buildReportMarkdown(state.report, meta));
      onSuccess?.(formatMessageDefault("cof.troubleshooting.ssetest.export.copied", "Report copied to clipboard."));
    } catch (e) {
      onError?.(e instanceof Error ? e : new Error("Copy failed"));
    }
  }, [state.report, meta, onError, onSuccess]);

  const isRunning = state.runPhase === "running";

  const overallBarType = (overall: string): MessageBarType => {
    if (overall === "PASS") return MessageBarType.success;
    if (overall === "FAIL") return MessageBarType.error;
    return MessageBarType.warning;
  };

  return (
    <div style={{ padding: "1rem" }}>
      <p style={{ margin: "0.5rem 0", color: "var(--color-black)" }}>
        {formatMessageDefault(
          "cof.troubleshooting.ssetest.description",
          "Checks whether this machine's network (proxies, firewalls) lets the Server-Sent Events channel work: no idle/read/first-byte timeout under 5 minutes and no response buffering."
        )}
      </p>

      <ChoiceGroup
        label={formatMessageDefault("cof.troubleshooting.ssetest.mode.label", "Test mode")}
        selectedKey={state.mode}
        options={modeOptions}
        disabled={isRunning}
        onChange={(_ev, option) => option && dispatch({ type: "SET_MODE", payload: option.key as TestMode })}
      />

      <MessageBar messageBarType={MessageBarType.info} isMultiline styles={{ root: { marginTop: "0.75rem" } }}>
        {formatMessageDefault(MODE_DESCRIPTIONS[state.mode].key, MODE_DESCRIPTIONS[state.mode].fallback)}
      </MessageBar>

      <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap", margin: "1rem 0" }}>
        <PrimaryButton
          text={formatMessageDefault("cof.troubleshooting.ssetest.button.start", "Start test")}
          onClick={runDiagnostic}
          disabled={isRunning}
          iconProps={{ iconName: "Play" }}
        />
        {state.report && !isRunning && (
          <>
            <DefaultButton
              text={formatMessageDefault("cof.troubleshooting.ssetest.button.export", "Export report")}
              onClick={exportMarkdown}
              iconProps={{ iconName: "Download" }}
            />
            <DefaultButton
              text={formatMessageDefault("cof.troubleshooting.ssetest.button.timeline", "Export timeline")}
              onClick={exportTimeline}
              iconProps={{ iconName: "Timeline" }}
            />
            <DefaultButton
              text={formatMessageDefault("cof.troubleshooting.ssetest.button.copy", "Copy")}
              onClick={copyReport}
              iconProps={{ iconName: "Copy" }}
            />
          </>
        )}
      </div>

      {isRunning && (
        <div style={{ padding: "1rem 0" }}>
          <ProgressIndicator
            label={
              state.statusText ||
              formatMessageDefault("cof.troubleshooting.ssetest.status.running", "Running SSE diagnostic...")
            }
            description={`${formatClock(state.elapsedMs)} / ~${formatClock(
              LOCAL_ESTIMATE_MS[state.mode]
            )} ${formatMessageDefault("cof.troubleshooting.ssetest.progress.max", "(max)")}`}
            percentComplete={Math.min(0.99, state.elapsedMs / LOCAL_ESTIMATE_MS[state.mode])}
          />
          <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
            <DefaultButton
              text={formatMessageDefault("cof.troubleshooting.ssetest.button.abort", "Abort")}
              onClick={abortDiagnostic}
              iconProps={{ iconName: "Cancel" }}
            />
          </div>
          <p style={{ marginTop: "0.5rem", fontSize: 12, color: "var(--color-grey)", textAlign: "center" }}>
            {formatMessageDefault(
              "cof.troubleshooting.ssetest.status.hint",
              "This can take several minutes on a healthy channel. A failing channel usually stops sooner."
            )}
          </p>
        </div>
      )}

      {state.error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          <strong>{formatMessageDefault("cof.troubleshooting.ssetest.error.prefix", "Error:")}</strong> {state.error}
        </MessageBar>
      )}

      {state.report && !isRunning && (
        <div style={{ marginBottom: "1.5rem" }}>
          <MessageBar messageBarType={overallBarType(state.report.overall)} isMultiline>
            <strong>{formatMessageDefault("cof.troubleshooting.ssetest.results.overall", "Overall verdict")}: </strong>
            {state.report.overall}
            {state.report.connectionError ? ` — ${state.report.connectionError}` : ""}
          </MessageBar>

          <Separator alignContent="start">
            <span style={{ fontWeight: 600, fontSize: 15 }}>
              {formatMessageDefault("cof.troubleshooting.ssetest.results.criteria", "Criteria")}
            </span>
          </Separator>

          {state.report.criteria.map((criterion) => (
            <div
              key={criterion.id}
              style={{
                display: "flex",
                gap: "0.6rem",
                padding: "0.5rem 0",
                borderBottom: "1px solid var(--color-lightblue)",
              }}
            >
              <Icon
                iconName={stateIcon(criterion.state)}
                style={{ fontSize: 18, color: stateColor(criterion.state), marginTop: 2 }}
              />
              <div>
                <div style={{ fontWeight: 600 }}>
                  {criterion.label}: {criterion.state}
                  {criterion.measuredMs !== undefined ? ` (${formatDuration(criterion.measuredMs)})` : ""}
                </div>
                <div style={{ fontSize: 12, color: "var(--color-grey)" }}>{criterion.detail}</div>
              </div>
            </div>
          ))}

          <p style={{ fontSize: 11, color: "var(--color-grey)", marginTop: "0.75rem" }}>
            {formatMessageDefault("cof.troubleshooting.ssetest.results.testId", "Test id")}: {state.report.testId}
          </p>
        </div>
      )}
    </div>
  );
};
